import styled, { css, keyframes } from "styled-components";

const shimmer = keyframes`
  0%   { background-position: -200px 0; }
  100% { background-position:  200px 0; }
`;

const shimmerStyle = css`
  background: linear-gradient(270deg, #eceef3 0%, #f3f4f7 100%);
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.65) 50%,
      transparent 100%
    );
    background-size: 200px 100%;
    background-repeat: no-repeat;
    animation: ${shimmer} 1.6s linear infinite;
  }
`;

/**
 * Skeleton loading layout (Figma node 3480:36765). Mirrors the loaded two-column
 * body: static "Currently live" / "Changes to publish" headers with the colored
 * dots, then placeholder rows in place of content while the diff is (re)computed.
 */
export const SkeletonBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: var(--color-gray-50, #f6f6f7);
`;

/* Fixed-height header so both skeleton columns start their rows at the same
   level, whether or not the "Published on" subtitle is present. */
export const SkeletonHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xxs, 4px);
  padding: var(--spacing-lg, 16px) var(--spacing-xl, 24px) 0;
  height: 56px;
  box-sizing: border-box;
`;

/* Rows sit flat on the column background (no bordered card, no dividers) and
   anchor to the top; the mask fades the stack out toward the bottom. */
export const SkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  height: 416px;
  padding: 8px 0;
  gap: 16px;
  justify-content: flex-start;
  align-items: stretch;
  margin: 0 var(--spacing-xl, 24px);
  overflow: hidden;
  mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
`;

export const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  height: 37px;
  gap: 24px;
  padding-right: 62px;
  flex-shrink: 0;
`;

export const SkeletonThumbnail = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e4e6eb;
`;

export const SkeletonRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 0 0;
`;

export const SkeletonBar = styled.span<{ $width: string; $height: string }>`
  ${shimmerStyle}
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};
  border-radius: 6.5px;
`;

export const StateContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md, 12px);
  min-height: 320px;
  padding: var(--spacing-xl, 24px);
  text-align: center;
`;

export const StateTitle = styled.span`
  align-self: stretch;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
  color: var(--color-gray-950, #19191b);
`;

export const StateDescription = styled.span`
  align-self: stretch;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-subtle, #7d7e84);
`;

/**
 * Dedicated no-change layout (Figma node 3525:5950): the icon and the
 * title/description text block are grouped with a tight 4px rhythm, centered in
 * the modal body — rather than the evenly-spaced shared state layout above.
 */
export const NoChangeContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xxs, 4px);
  min-height: 320px;
  padding: var(--spacing-xl, 24px);
  text-align: center;
`;

export const NoChangeTitle = styled.span`
  align-self: stretch;
  font-size: 1.25rem;
  font-weight: 900;
  line-height: 1.75rem;
  color: var(--color-gray-950, #19191b);
`;

export const NoChangeDescription = styled.span`
  align-self: stretch;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.75rem;
  /* Figma "System/text/dark-grey"; no matching token in the gray ramp. */
  color: #8b91a4;
`;
